/**
 * CookieBanner.tsx — GDPR/UK Cookie Consent Banner
 *
 * Displayed at the bottom of every page until the user accepts/configures.
 * Compliant with UK/EU GDPR requirements:
 *  - Shows on first visit
 *  - Persists choice in localStorage
 *  - Dispatches custom event so analytics can react
 *
 * Injected into Layout.astro globally.
 */
import { useState, useEffect, useCallback } from 'react';
import { getGdprConsentConfig, saveCookieConsent, type GdprConsentConfig } from '@agentic/magento-client/lib/gdpr';

interface CookiePreferences {
  necessary: boolean;    // Always true – required
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: string;
}

const STORAGE_KEY = 'gdpr_consent';

function getStoredConsent(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookiePreferences;
  } catch {
    return null;
  }
}

/**
 * Push the consent decision to Google Consent Mode v2 via the SAME event the
 * analytics loader (Analytics.astro) listens for: `agentic:consent-update` with
 * `{ analytics, marketing }`. Previously this banner only emitted the legacy
 * `gdpr-consent-changed` event, which nothing on the analytics side listened
 * for — so accepting cookies never granted GA4 consent and every hit stayed
 * `gcs=G100` (denied), producing "No data received" in GA4. Emit both: the
 * Consent Mode event AND the legacy one for any other listeners.
 */
function applyConsentToAnalytics(c: { analytics: boolean; marketing: boolean }): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('agentic:consent-update', {
      detail: { analytics: c.analytics, marketing: c.marketing },
    })
  );
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
    timestamp: '',
  });

  // GdprPro module config (categories, policy URLs, enabled gate). Null until
  // loaded or if the module is unreachable — banner still works locally.
  const [gdprConfig, setGdprConfig] = useState<GdprConsentConfig | null>(null);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Small delay for better UX — don't flash on initial load
      setTimeout(() => setVisible(true), 1500);
    } else {
      // Returning visitor: re-apply their stored choice to Consent Mode on every
      // page load. The gtag consent DEFAULT is `denied`, so without re-applying,
      // a visitor who previously accepted would silently revert to denied on the
      // next page and GA4 would stop receiving consented hits.
      applyConsentToAnalytics({ analytics: !!stored.analytics, marketing: !!stored.marketing });
    }
    // Pull the merchant's GDPR config from Magento (GdprPro). If the module
    // reports the feature disabled, never show the banner — the merchant has
    // turned consent collection off.
    void getGdprConsentConfig().then((cfg) => {
      if (!cfg) return;
      setGdprConfig(cfg);
      if (cfg.enabled === false) setVisible(false);
    });
  }, []);

  const saveConsent = useCallback((consent: CookiePreferences) => {
    const withTimestamp = { ...consent, timestamp: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(withTimestamp));
    setVisible(false);

    // Grant/deny Google Consent Mode v2 via the event the analytics loader
    // listens for (this is what actually lets GA4 fire consented hits)...
    applyConsentToAnalytics({ analytics: consent.analytics, marketing: consent.marketing });
    // ...and keep the legacy event for any other listeners.
    window.dispatchEvent(
      new CustomEvent('gdpr-consent-changed', { detail: withTimestamp })
    );

    // Log the choice server-side for the GdprPro audit trail (best-effort —
    // the local store above already applied it, so a failed log won't block).
    void saveCookieConsent({
      necessary: consent.necessary,
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences,
    });
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: '',
    });
  }, [saveConsent]);

  const rejectNonEssential = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: '',
    });
  }, [saveConsent]);

  const saveCustom = useCallback(() => {
    saveConsent(prefs);
  }, [prefs, saveConsent]);

  if (!visible) return null;

  return (
    <div
      className="cookie-consent-banner fixed left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div
        className="mx-auto max-w-5xl mb-4 mx-4 sm:mx-6 lg:mx-auto overflow-hidden"
        style={{
          backgroundColor: 'var(--color-cookie-banner-bg, var(--style-modal-bg, var(--surface-1, #0a0a0a)))',
          borderRadius: 'var(--style-modal-radius, var(--style-card-radius, 1rem))',
          borderTop: '1px solid var(--glass-border, rgba(255,255,255,0.1))',
          border: '1px solid var(--glass-border, rgba(255,255,255,0.1))',
          boxShadow: 'var(--style-modal-shadow, var(--style-elevated-shadow, 0 -8px 40px rgba(0,0,0,0.4)))',
          backdropFilter: 'var(--style-backdrop-filter, blur(20px))',
          WebkitBackdropFilter: 'var(--style-backdrop-filter, blur(20px))',
        }}
      >
        {/* Main Banner */}
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Icon + Text */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🍪</span>
                <h2 className="text-sm font-bold" style={{ color: 'var(--color-cookie-banner-text, var(--text-primary, #f1f5f9))' }}>
                  Cookie Preferences
                </h2>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--color-cookie-banner-text, var(--text-secondary, #94a3b8))' }}>
                We use cookies to enhance your experience, analyse traffic, and serve targeted content.
                You can accept all cookies, reject non-essential ones, or customise your preferences.
                By continuing to use this site you consent to our use of cookies in accordance with our{' '}
                <a href={gdprConfig?.privacy_policy_url || '/privacy-policy'} className="hover:underline" style={{ color: 'var(--color-link-color, var(--accent-primary, #6366f1))' }}>
                  Privacy Policy
                </a>.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap sm:flex-col gap-2 flex-shrink-0">
              <button
                onClick={acceptAll}
                className="px-5 py-2.5 text-xs font-bold hover:opacity-90 transition-opacity"
                style={{
                  borderRadius: 'var(--style-btn-radius, 8px)',
                  backgroundColor: 'var(--color-cookie-banner-button-bg, var(--color-cta-primary-bg, var(--accent-primary, #6366f1)))',
                  color: 'var(--color-cookie-banner-button-text, var(--color-cta-primary-text, var(--text-on-accent, #ffffff)))',
                }}
              >
                Accept All
              </button>
              <button
                onClick={rejectNonEssential}
                className="px-5 py-2.5 border border-[var(--glass-border,rgba(255,255,255,0.1))] text-[var(--text-primary,#f1f5f9)] text-xs font-bold hover:bg-[color-mix(in_srgb,var(--surface-1)_5%,transparent)] transition-colors"
                style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}
              >
                Reject Non-Essential
              </button>
              <button
                onClick={() => setShowPreferences(!showPreferences)}
                className="px-5 py-2.5 text-[var(--text-muted,#64748b)] text-xs font-medium hover:text-[var(--text-primary,#f1f5f9)] transition-colors underline-offset-2 hover:underline"
                style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}
              >
                {showPreferences ? 'Hide Options' : 'Customise'}
              </button>
            </div>
          </div>

          {/* Expandable Preferences Panel */}
          {showPreferences && (
            <div className="mt-4 pt-4 border-t border-[var(--glass-border,rgba(255,255,255,0.06))]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Necessary — always on */}
                <label className="flex items-center gap-3 p-3 bg-[color-mix(in_srgb,var(--surface-1)_2%,transparent)] cursor-not-allowed" style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}>
                  <input type="checkbox" checked disabled className="accent-green-500 w-4 h-4" />
                  <div>
                    <span className="text-xs font-semibold text-[var(--text-primary,#f1f5f9)]">Necessary</span>
                    <p className="text-[10px] text-[var(--text-muted,#64748b)]">Required for site functionality</p>
                  </div>
                </label>

                {/* Analytics */}
                <label className="flex items-center gap-3 p-3 bg-[color-mix(in_srgb,var(--surface-1)_2%,transparent)] cursor-pointer hover:bg-[color-mix(in_srgb,var(--surface-1)_4%,transparent)] transition-colors" style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                    className="accent-[var(--accent-primary,#6366f1)] w-4 h-4"
                  />
                  <div>
                    <span className="text-xs font-semibold text-[var(--text-primary,#f1f5f9)]">Analytics</span>
                    <p className="text-[10px] text-[var(--text-muted,#64748b)]">Help us understand usage</p>
                  </div>
                </label>

                {/* Marketing */}
                <label className="flex items-center gap-3 p-3 bg-[color-mix(in_srgb,var(--surface-1)_2%,transparent)] cursor-pointer hover:bg-[color-mix(in_srgb,var(--surface-1)_4%,transparent)] transition-colors" style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}>
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                    className="accent-[var(--accent-primary,#6366f1)] w-4 h-4"
                  />
                  <div>
                    <span className="text-xs font-semibold text-[var(--text-primary,#f1f5f9)]">Marketing</span>
                    <p className="text-[10px] text-[var(--text-muted,#64748b)]">Personalised advertising</p>
                  </div>
                </label>

                {/* Preferences */}
                <label className="flex items-center gap-3 p-3 bg-[color-mix(in_srgb,var(--surface-1)_2%,transparent)] cursor-pointer hover:bg-[color-mix(in_srgb,var(--surface-1)_4%,transparent)] transition-colors" style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}>
                  <input
                    type="checkbox"
                    checked={prefs.preferences}
                    onChange={(e) => setPrefs({ ...prefs, preferences: e.target.checked })}
                    className="accent-[var(--accent-primary,#6366f1)] w-4 h-4"
                  />
                  <div>
                    <span className="text-xs font-semibold text-[var(--text-primary,#f1f5f9)]">Preferences</span>
                    <p className="text-[10px] text-[var(--text-muted,#64748b)]">Remember your settings</p>
                  </div>
                </label>
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  onClick={saveCustom}
                  className="px-5 py-2 bg-[var(--accent-primary,#6366f1)] text-[var(--text-primary)] text-xs font-bold hover:opacity-90 transition-opacity"
                  style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
